import { Modal } from "antd";
import { useEffect, useState } from "react";
import { useHistory } from "react-router";
import { TEditingEntity } from "components/feature-entity-linking-page/models";

const createUnsaveConfirmPromise = (
  onSave: () => Promise<void>
): Promise<"save" | "discard" | "cancel"> => {
  return new Promise((resolve) => {
    const modal = Modal.confirm({
      title: "Unsaved Changes",
      content:
        "You are attempting to leave a page which has unsaved changes. Do you want to save the changes before exiting?",
      style: { top: 10, width: 240 },
      okText: "Save and Exit",
      cancelText: "Discard",
      closable: true,
      className: "skip-listing-window custom-rounded-modal",

      async onOk() {
        await onSave();
        resolve("save");
      },
      onCancel() {
        resolve("discard");
      },
      afterClose() {
        modal.destroy();
      }
    });
  });
};

export function UnsaveConfirmModal({
  onSave,
  editingEntity
}: {
  onSave: () => Promise<void>;
  editingEntity: TEditingEntity | null;
}) {
  const history = useHistory();

  const [isEditingMode, setEditingMode] = useState<boolean>(!!editingEntity);

  useEffect(() => {
    const newValue = !!editingEntity;
    setEditingMode((prev) => {
      if (prev !== newValue) {
        return newValue; // only update if it actually changed
      }
      return prev; // avoid redundant state update
    });
  }, [editingEntity]);

  useEffect(() => {
    if (!isEditingMode) {
      return () => {}; // noop cleanup
    }

    const unblock = history.block(({ pathname }) => {
      createUnsaveConfirmPromise(onSave).then((action) => {
        // createUnsaveConfirmPromise resolves only "save" (onOk) or "discard"
        // (onCancel) — it never resolves "cancel" — so the false arm of this
        // guard cannot execute.
        /* v8 ignore next */
        if (action === "save" || action === "discard") {
          unblock();
          history.push(pathname);
        }
      });
      return false;
    });

    return () => {
      unblock();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditingMode]);

  return null;
}
